import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Alert, CircularProgress, Box, Button } from '@mui/material';

import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { filterSelector, bookingModeSelector } from 'helper/filter';
import { userSelector, hasAnyAdminSpace } from 'helper/security';
import { EBookingMode } from 'types';

import SimpleAccordion from 'components/SimpleAccordion';
import SimpleTable from 'components/table/SimpleTable';
import PagedTable from 'components/table/PagedTable';
import { FormatDateRange } from 'components/DateTime';

import { PRICELISTS_LIST_QUERY, HOTEL_WITH_SPACE_DETAILS_QUERY } from './gql';

/**
 * Price lists list page with pagination and filtering by space and booking mode.
 * Supports auto-navigation to hotel's pricelist when only one exists.
 * Shows published and unpublished pricelists separately.
 */
export default function PriceListsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const filter = filterSelector();
  const bookingMode = bookingModeSelector();
  const user = userSelector()!;

  const [publishedQuery, { data: publishedData, loading: publishedLoading, error: publishedError }] = useLazyQuery(PRICELISTS_LIST_QUERY, {
    fetchPolicy: 'network-only', // Always fetch fresh data from network, ensures new pricelists appear after creation
  });
  const [hotelQuery, { data: hotelData, loading: hotelLoading }] = useLazyQuery(HOTEL_WITH_SPACE_DETAILS_QUERY);
  const [isEvaluatingAutoNav, setIsEvaluatingAutoNav] = React.useState(false);
  const [hasNavigatedAway, setHasNavigatedAway] = React.useState(false);
  // Synchronous guard to prevent race-condition redirects on user navigation (e.g., Import)
  const hasNavigatedAwayRef = React.useRef(false);

  // Track if user has navigated away from the list page
  React.useEffect(() => {
    const pathParts = location.pathname.split('/').filter(p => p);
    const isOnSubRoute = pathParts.length > 2 || (pathParts.length === 2 && pathParts[1] !== 'pricelists');

    if (isOnSubRoute) {
      setHasNavigatedAway(true);
    } else {
      // Reset when back on list page
      setHasNavigatedAway(false);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (bookingMode === EBookingMode.SEM) {
      // For SEM mode, fetch all pricelists (published and unpublished)
      // Don't filter by hotelId - show all pricelists including those without a hotel
      publishedQuery({
        variables: {
          spaceId: (filter && filter.spaceId) || null,
          hotelId: null,
          isPublished: undefined,
          skip: 0,
          take: 100,
          forLister: false,
          forWidget: true,
        },
      });
      setIsEvaluatingAutoNav(false);
    } else if (bookingMode === EBookingMode.EPILO) {
      // For Epilo mode with hotelId: need to fetch for auto-navigation logic
      // For Epilo mode without hotelId: PagedTable handles fetching via refetchPage
      if (filter && filter.hotelId && !hasNavigatedAwayRef.current) {
        publishedQuery({
          variables: {
            spaceId: (filter && filter.spaceId) || null,
            hotelId: filter.hotelId,
            isPublished: true,
            skip: 0,
            take: 100,
            forLister: true,
            forWidget: false,
          },
        });
        hotelQuery({
          variables: {
            id: filter.hotelId,
          },
        });
        setIsEvaluatingAutoNav(true);
      } else {
        // No hotelId selected - PagedTable will handle the fetch
        setIsEvaluatingAutoNav(false);
      }
    } else {
      setIsEvaluatingAutoNav(false);
    }
  }, [filter, bookingMode, hasNavigatedAway]);

  // Auto-navigate to appropriate pricelist when hotel is selected - only for Epilo mode
  React.useEffect(() => {
    // Skip auto-navigation for SEM mode
    if (bookingMode !== EBookingMode.EPILO) {
      setIsEvaluatingAutoNav(false);
      return;
    }

    // Skip if user has navigated away (to import, create, etc)
    if (hasNavigatedAwayRef.current) {
      setIsEvaluatingAutoNav(false);
      return;
    }

    // Only auto-navigate when on the list page (not on import, create, or detail pages)
    const pathParts = location.pathname.split('/').filter(p => p);
    const isOnSubRoute = pathParts.length > 2 || (pathParts.length === 2 && pathParts[1] !== 'pricelists');
    if (isOnSubRoute) {
      setIsEvaluatingAutoNav(false);
      return;
    }

    if (!filter?.hotelId) {
      setIsEvaluatingAutoNav(false);
      return;
    }

    if (publishedLoading || hotelLoading) {
      setIsEvaluatingAutoNav(true);
      return;
    }

    if (!publishedData?.listPriceLists?.priceLists || !hotelData?.viewHotel) {
      setIsEvaluatingAutoNav(true);
      return;
    }

    const hotel = hotelData.viewHotel;
    const space = hotel.space;
    const pricelists = publishedData.listPriceLists.priceLists;

    let targetPricelist = null;

    // Navigate for Epilo mode only
    if (space.hasEpilo) {
      targetPricelist = pricelists.find(pl => pl.isPublished && pl.forLister);
    }

    if (targetPricelist) {
      navigate(`/pricelists/pricelists/${targetPricelist.id}`);
      return;
    }

    setIsEvaluatingAutoNav(false);
    // If no suitable pricelist found, menu item should be disabled (handled in main.tsx)
  }, [filter?.hotelId, publishedLoading, publishedData, hotelLoading, hotelData, navigate, bookingMode, location.pathname, hasNavigatedAway]);

  const publishedReady = Boolean(publishedData) || Boolean(publishedError);
  const initialDataPending = !publishedReady;

  // For Epilo mode without hotelId, PagedTable handles loading state internally
  // For Epilo mode with hotelId, we need to wait for auto-navigation data
  const isLoading = bookingMode === EBookingMode.EPILO && !filter?.hotelId
    ? false  // PagedTable handles its own loading
    : isEvaluatingAutoNav || publishedLoading || hotelLoading || initialDataPending;

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>{i18next.t('pricelists-list-page-title')}</title>
        </Helmet>
        <Box sx={{ padding: 3 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  // Render different views based on booking mode
  const renderSEMView = () => {
    if (publishedError) {
      return <Alert severity="error">{publishedError.message}</Alert>;
    }

    const allPricelists = publishedData?.listPriceLists?.priceLists || [];
    const publishedPricelists = allPricelists.filter(pl => pl.isPublished);
    const unpublishedPricelists = allPricelists.filter(pl => !pl.isPublished);

    const buildTableData = (pricelists: any[]) => {
      return pricelists.map(row => [
        <Link to={`/pricelists/pricelists/${row.id}`}>{row.name}</Link>,
        row.refCode,
        row.isPublished ? <CheckIcon /> : null,
        row.isDefault ? <CheckIcon /> : null,
        row.version,
        <FormatDateRange from={row.validity?.validFrom} to={row.validity?.validTo} />,
        row.hotel ? <Link to={`/settings/hotels/${row.hotel.id}`}>{row.hotel.name}</Link> : '',
        !user.isSingleSpace ? row.space.name : null,
      ].filter((_, index) => user.isSingleSpace ? index !== 7 : true));
    };

    const headers = [
      i18next.t('pricelists-list-header-name'),
      i18next.t('pricelists-list-header-refcode'),
      i18next.t('pricelists-list-header-ispublished'),
      i18next.t('pricelists-list-header-isdefault'),
      i18next.t('pricelists-list-header-version'),
      i18next.t('pricelists-list-header-validity'),
      i18next.t('pricelists-list-header-hotel'),
      !user.isSingleSpace ? i18next.t('list-header-space') : null,
    ].filter(h => h !== null);

    return (
      <>
        <SimpleAccordion header={String(i18next.t('pricelists-list-published'))} autoOpen={true}>
          <SimpleTable
            headers={headers}
            rows={buildTableData(publishedPricelists)}
            fullWidth={true}
          />
        </SimpleAccordion>

        {hasAnyAdminSpace(user) && (
          <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!filter?.spaceId}
              onClick={() => {
                hasNavigatedAwayRef.current = true;
                setHasNavigatedAway(true);
                navigate('pricelists/_create');
              }}
            >
              {i18next.t('add')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ImportExportIcon />}
              disabled={!filter?.spaceId}
              onClick={() => {
                hasNavigatedAwayRef.current = true;
                setHasNavigatedAway(true);
                navigate('import');
              }}
            >
              {i18next.t('import')}
            </Button>
          </Box>
        )}

        <SimpleAccordion header={String(i18next.t('pricelists-list-notpublished'))} autoOpen={true}>
          <SimpleTable
            headers={headers}
            rows={buildTableData(unpublishedPricelists)}
            fullWidth={true}
          />
        </SimpleAccordion>
      </>
    );
  };

  const renderEpiloView = () => {
    const headers = [
      i18next.t('pricelists-list-header-name'),
      i18next.t('pricelists-list-header-refcode'),
      i18next.t('pricelists-list-header-ispublished'),
      i18next.t('pricelists-list-header-isdefault'),
      i18next.t('pricelists-list-header-hotel'),
      !user.isSingleSpace ? i18next.t('list-header-space') : null,
    ].filter(h => h !== null) as string[];

    const rows = (publishedData?.listPriceLists?.priceLists || []).map(row => [
      <Link to={`/pricelists/pricelists/${row.id}`}>{row.name}</Link>,
      row.refCode,
      row.isPublished ? <CheckIcon /> : null,
      row.isDefault ? <CheckIcon /> : null,
      row.hotel ? <Link to={`/settings/hotels/${row.hotel.id}`}>{row.hotel.name}</Link> : '',
      !user.isSingleSpace ? row.space.name : null,
    ].filter((_, idx) => user.isSingleSpace ? idx !== 5 : true));

    return (
      <PagedTable
        headers={headers}
        rows={rows}
        rowsLoading={publishedLoading}
        rowsErr={publishedError}
        totalCount={publishedData?.listPriceLists?.totalCount}
        refetchPage={(skip, take) => {
          publishedQuery({
            variables: {
              spaceId: filter?.spaceId || null,
              hotelId: filter?.hotelId || null,
              isPublished: true,
              skip,
              take,
              forLister: true,
              forWidget: false,
            },
          });
        }}
        filterKey={`${filter?.spaceId}-${filter?.hotelId}`}
        defaultPageSize={10}
      />
    );
  };

  return (
    <>
      <Helmet>
        <title>{i18next.t('pricelists-list-page-title')}</title>
      </Helmet>
      {bookingMode === EBookingMode.SEM ? renderSEMView() : renderEpiloView()}
    </>
  );
}
